/**
 * Equipment Automation — pure-logic module for AC calculation and attack row building.
 * No side effects, no server dependencies.
 */

import type { RepeatingSectionRow } from '../../types.js';
import type { SrdEquipmentItem } from './srd/types.js';

// --- Inventory helpers ---

export interface InventoryItem {
  rowId: string;
  name: string;
  srd_slug: string;
  item_type: string;
  equipped: boolean;
  /** #1B (Option B): manual base AC for armor tracked without an SRD slug. */
  base_ac?: number;
  /** #1B (Option B): armor weight class governing the DEX cap. */
  armor_category?: 'Light' | 'Medium' | 'Heavy';
}

/** Extract structured inventory items from repeatingData rows. */
export function parseInventory(rows: RepeatingSectionRow[]): InventoryItem[] {
  return rows.map(r => {
    const rawBaseAc = Number(r.values['base_ac']);
    const rawCat = String(r.values['armor_category'] ?? 'Light');
    return {
      rowId: r.rowId,
      name: String(r.values['name'] ?? ''),
      srd_slug: String(r.values['srd_slug'] ?? ''),
      item_type: String(r.values['item_type'] ?? 'gear'),
      equipped: Boolean(r.values['equipped']),
      base_ac: Number.isFinite(rawBaseAc) ? rawBaseAc : 0,
      armor_category: rawCat === 'Medium' || rawCat === 'Heavy' ? rawCat : 'Light',
    };
  });
}

/**
 * Find the equipped armor item (not shield). Returns undefined if none.
 *
 * Resolution order: (1) SRD catalogue via `srd_slug`; (2) Option-B manual armor
 * synthesised from `base_ac` + `armor_category` when no SRD entry resolves. The
 * synthetic object is shaped exactly like an {@link SrdEquipmentItem} so that
 * {@link calculateAC} / {@link computeAcBreakdown} apply the same DEX-cap logic:
 *   Light  → acMaxBonus null  (full DEX)
 *   Medium → acMaxBonus 2     (DEX capped at +2)
 *   Heavy  → acMaxBonus 0 / acDexBonus false (no DEX)
 */
export function findEquippedArmor(
  inventory: InventoryItem[],
  equipmentMap: Map<string, SrdEquipmentItem>,
): SrdEquipmentItem | undefined {
  for (const item of inventory) {
    if (!item.equipped) continue;
    if (item.item_type !== 'armor') continue;
    const srd = equipmentMap.get(item.srd_slug);
    if (srd && srd.armorCategory && srd.armorCategory !== 'Shield') return srd;
    // Option B: no resolvable SRD armor → synthesise from manual base AC.
    if (!srd && typeof item.base_ac === 'number' && item.base_ac > 0) {
      const category = item.armor_category ?? 'Light';
      return {
        slug: item.srd_slug || `manual-armor-${item.rowId}`,
        name: item.name,
        category: 'Armor',
        cost: null,
        weight: null,
        armorCategory: category,
        acBase: item.base_ac,
        acDexBonus: category !== 'Heavy',
        acMaxBonus: category === 'Medium' ? 2 : category === 'Heavy' ? 0 : null,
      };
    }
  }
  return undefined;
}

/** Find all equipped weapon items with their SRD data. */
export function findEquippedWeapons(
  inventory: InventoryItem[],
  equipmentMap: Map<string, SrdEquipmentItem>,
): Array<{ item: InventoryItem; srd: SrdEquipmentItem }> {
  const result: Array<{ item: InventoryItem; srd: SrdEquipmentItem }> = [];
  for (const item of inventory) {
    if (!item.equipped) continue;
    if (item.item_type !== 'weapon') continue;
    const srd = equipmentMap.get(item.srd_slug);
    if (srd) result.push({ item, srd });
  }
  return result;
}

/** Check if a shield is equipped. */
export function hasEquippedShield(
  inventory: InventoryItem[],
  equipmentMap: Map<string, SrdEquipmentItem>,
): boolean {
  for (const item of inventory) {
    if (!item.equipped) continue;
    if (item.item_type !== 'shield') continue;
    const srd = equipmentMap.get(item.srd_slug);
    if (srd && srd.armorCategory === 'Shield') return true;
    // Option B: manual shield without an SRD slug — flat +2 downstream. The
    // shield bonus is applied once (`hasShield || manual.shieldOn`) so this
    // never double-counts with the server's separate `shield_on` knob.
    if (!srd) return true;
  }
  return false;
}

// --- AC Calculation ---

export interface ClassFeatures {
  unarmoredDefense?: 'barbarian' | 'monk' | 'draconic';
  /** Fighting Style: Defense (+1 AC in armor). */
  defenseStyle?: boolean;
}

/**
 * Calculate AC per D&D 5e rules.
 * - No armor: 10 + DEX mod
 * - Light armor: acBase + DEX mod
 * - Medium armor: acBase + min(DEX mod, acMaxBonus ?? 2)
 * - Heavy armor: acBase (no DEX)
 * - Shield: +2
 * - Unarmored Defense (Barbarian): 10 + DEX mod + CON mod
 * - Unarmored Defense (Monk): 10 + DEX mod + WIS mod
 */
export function calculateAC(
  armor: SrdEquipmentItem | undefined,
  hasShield: boolean,
  dexMod: number,
  classFeatures?: ClassFeatures,
  conMod?: number,
  wisMod?: number,
): number {
  let ac: number;

  if (armor) {
    const base = armor.acBase ?? 10;
    const category = armor.armorCategory ?? '';

    if (category === 'Heavy') {
      ac = base;
    } else if (category === 'Medium') {
      const cap = armor.acMaxBonus ?? 2;
      ac = base + Math.min(dexMod, cap);
    } else {
      // Light armor: full DEX
      ac = base + dexMod;
    }
  } else {
    // No armor — check Unarmored Defense
    if (classFeatures?.unarmoredDefense === 'barbarian') {
      ac = 10 + dexMod + (conMod ?? 0);
    } else if (classFeatures?.unarmoredDefense === 'monk') {
      ac = 10 + dexMod + (wisMod ?? 0);
    } else if (classFeatures?.unarmoredDefense === 'draconic') {
      ac = 13 + dexMod;
    } else {
      ac = 10 + dexMod;
    }
  }

  if (hasShield) {
    ac += 2;
  }
  if (armor && classFeatures?.defenseStyle) ac += 1;

  return ac;
}

/**
 * Detailed AC breakdown — the same arithmetic as {@link calculateAC} but with
 * each contribution itemised, plus a manual `misc` addend. The pieces are
 * guaranteed to sum to `total`:
 *
 *   total = base + appliedDex + shieldBonus + unarmoredBonus + miscBonus
 *
 * `total` equals `calculateAC(armor, hasShield, dexMod, …) + miscAc`, so server
 * (handleEquipChange) and client (hud-adapter) derive an identical authoritative
 * AC from the same inputs — no divergence. Active-effect AC bonuses (Shield spell,
 * magic items) are NOT included here; they are layered on top of the stored
 * `sheet.values.ac` server-side and surfaced separately as an "effects" residual.
 */
export interface AcBreakdownDetail {
  base: number;
  armorName: string | null;
  /** DEX contribution after armor-category cap (0 for heavy / no-dex armor). */
  appliedDex: number;
  /** Manual additive armor bonus (`armor_ac`) — for characters who don't track
   * SRD armor as inventory. Added on top of `base`. May be negative. */
  armorBonus: number;
  shieldBonus: number;
  /** Barbarian CON / Monk WIS / Draconic +3 unarmored-defense bonus (only when no armor). */
  unarmoredBonus: number;
  /** Fighting Style: Defense, +1 while armor is worn. */
  styleBonus: number;
  /** Manual `misc_ac` addend (rings, feats, cover, …). May be negative. */
  miscBonus: number;
  /** base + armorBonus + appliedDex + shieldBonus + unarmoredBonus + miscBonus. */
  total: number;
}

/** Manual AC knobs that supplement inventory derivation (#2 AC). */
export interface AcManualBonuses {
  /** Additive armor bonus when armor isn't tracked as SRD inventory. */
  armorBonus?: number;
  /** Manual shield toggle — +2 (OR'd with an equipped inventory shield). */
  shieldOn?: boolean;
}

/**
 * Itemise AC from raw inventory + ability mods + manual addends.
 * Pure; used by both the server (sheet-helpers.handleEquipChange) and the
 * client (Dnd5eSheetAdapter) so the breakdown shown matches the stored AC.
 */
export function computeAcBreakdown(
  inventoryRows: RepeatingSectionRow[],
  equipmentMap: Map<string, SrdEquipmentItem>,
  dexMod: number,
  miscAc: number,
  classFeatures?: ClassFeatures,
  conMod?: number,
  wisMod?: number,
  manual?: AcManualBonuses,
): AcBreakdownDetail {
  const inventory = parseInventory(inventoryRows);
  const armor = findEquippedArmor(inventory, equipmentMap);
  const hasShield = hasEquippedShield(inventory, equipmentMap);

  let base: number;
  let armorName: string | null;
  let appliedDex: number;
  let unarmoredBonus = 0;

  if (armor) {
    base = armor.acBase ?? 10;
    armorName = armor.name;
    const category = armor.armorCategory ?? '';
    if (category === 'Heavy') {
      appliedDex = 0;
    } else if (category === 'Medium') {
      appliedDex = Math.min(dexMod, armor.acMaxBonus ?? 2);
    } else {
      appliedDex = dexMod;
    }
  } else {
    base = 10;
    armorName = null;
    appliedDex = dexMod;
    if (classFeatures?.unarmoredDefense === 'barbarian') unarmoredBonus = conMod ?? 0;
    else if (classFeatures?.unarmoredDefense === 'monk') unarmoredBonus = wisMod ?? 0;
    // Draconic Resilience (SRD sorcerer, Draconic Bloodline): unarmored AC = 13 + DEX.
    else if (classFeatures?.unarmoredDefense === 'draconic') unarmoredBonus = 3;
  }
  // Fighting Style: Defense — +1 AC while wearing armor (PHB p.72).
  const styleBonus = armor && classFeatures?.defenseStyle ? 1 : 0;

  // The manual `armor_ac` addend stands in for armor NOT tracked as an inventory
  // item. When an armor item IS equipped it already supplies `base`, so counting
  // the manual bonus too would double the armor (the UI hides the manual control
  // in that case; here we make the arithmetic agree). Drop it when armor is worn.
  const armorBonus = armor ? 0 : (manual?.armorBonus ?? 0);
  const shieldBonus = (hasShield || manual?.shieldOn) ? 2 : 0;
  const total = base + armorBonus + appliedDex + shieldBonus + unarmoredBonus + styleBonus + miscAc;

  return { base, armorName, appliedDex, armorBonus, shieldBonus, unarmoredBonus, styleBonus, miscBonus: miscAc, total };
}

/**
 * The class features that change AC, read from a sheet: barbarian/monk Unarmored Defense, the draconic sorcerer's
 * Draconic Resilience, the Defense fighting style. One reader for the server's stored AC, the HUD breakdown and the
 * character builder, so the three agree.
 */
export function acClassFeatures(
  classData: ReadonlyArray<{ classSlug: string; subclassSlug?: string | null }> | undefined,
  values: Record<string, unknown>,
): ClassFeatures {
  const features: ClassFeatures = {};
  for (const cd of classData ?? []) {
    if (cd.classSlug === 'barbarian') { features.unarmoredDefense = 'barbarian'; break; }
    if (cd.classSlug === 'monk') { features.unarmoredDefense = 'monk'; break; }
    if (cd.classSlug === 'sorcerer' && cd.subclassSlug === 'draconic') { features.unarmoredDefense = 'draconic'; break; }
  }
  if (values['fighting_style'] === 'defense') features.defenseStyle = true;
  return features;
}

/**
 * Check if armor has a STR requirement penalty.
 * Returns true if the character doesn't meet the strMinimum.
 */
export function hasArmorStrPenalty(armor: SrdEquipmentItem, str: number): boolean {
  return (armor.strMinimum ?? 0) > str;
}

// --- Attack Row Building ---

/** «Crossbows, light» / «Crossbow, light» / «Light crossbows» → «crossbow light» (sorted singular words). */
function weaponWords(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-zа-яё ]+/gi, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(w => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w))
    .sort()
    .join(' ');
}

/**
 * Whether a proficiency list (the sheet's `values.proficiencies`: class + race + background grants, free text in EN or
 * RU) covers this weapon — by its category («Simple Weapons», «Воинское оружие») or by its own name («Longswords»,
 * «Crossbows, light»). No list at all (a sheet made before proficiencies were recorded) counts as proficient: the old
 * rows always added the bonus and an empty list must not take it away silently.
 */
export function isWeaponProficient(weapon: SrdEquipmentItem, proficiencies: readonly string[] | undefined | null): boolean {
  if (!proficiencies || proficiencies.length === 0) return true;
  const category = (weapon.weaponCategory ?? '').toLowerCase();
  const own = weaponWords(weapon.name);
  for (const raw of proficiencies) {
    const p = raw.toLowerCase().trim();
    if (category === 'simple' && (/simple weapon/.test(p) || /простое оружие/.test(p))) return true;
    if (category === 'martial' && (/martial weapon/.test(p) || /воинское оружие/.test(p))) return true;
    if (weaponWords(p) === own) return true;
  }
  return false;
}

export interface AttackRowOptions {
  /** Proficient with the weapon (adds the proficiency bonus to hit). Default true. */
  proficient?: boolean;
  /** The fighting style slug (values.fighting_style): archery +2 to hit with ranged weapons, dueling +2 damage with a
   * one-handed melee weapon (PHB p.72). */
  fightingStyle?: string | null;
  /** Another weapon is equipped too — Dueling needs «no other weapons». */
  otherWeaponEquipped?: boolean;
}

/** The ability a weapon attacks with: finesse → the better of STR/DEX, ranged → DEX, else STR (PHB p.194). */
export function weaponAbilityMod(weapon: SrdEquipmentItem, strMod: number, dexMod: number): number {
  const isFinesse = weapon.properties?.includes('Finesse') ?? false;
  const isRanged = weapon.weaponRange === 'Ranged';
  if (isFinesse) return Math.max(strMod, dexMod);
  if (isRanged) return dexMod;
  return strMod;
}

/**
 * Build an attack row from a weapon's SRD data + character stats: to hit = the ability mod (+ the proficiency bonus when
 * proficient), damage = the weapon die + the same ability mod (PHB p.196). The row id is `weapon-<slug>` — one row per
 * weapon, the key the equip sync keeps in step.
 */
export function buildAttackRow(
  weapon: SrdEquipmentItem,
  strMod: number,
  dexMod: number,
  profBonus: number,
  options: AttackRowOptions = {},
): RepeatingSectionRow {
  const isRanged = weapon.weaponRange === 'Ranged';
  const abilityMod = weaponAbilityMod(weapon, strMod, dexMod);
  const proficient = options.proficient ?? true;
  const style = options.fightingStyle ?? null;

  let toHit = abilityMod + (proficient ? profBonus : 0);
  if (style === 'archery' && isRanged) toHit += 2;
  const damageDice = weapon.damageDice ?? '1d4';
  const twoHanded = weapon.properties?.includes('Two-Handed') ?? false;
  const damageBonus = abilityMod + (style === 'dueling' && !isRanged && !twoHanded && !options.otherWeaponEquipped ? 2 : 0);
  const damageType = weapon.damageType?.toLowerCase() ?? 'bludgeoning';

  // Build properties string
  const props: string[] = [];
  // «Versatile (1d10)»: the two-handed die is shown with the property
  if (weapon.properties) props.push(...weapon.properties.map(p => (p === 'Versatile' && weapon.twoHandedDice ? `Versatile (${weapon.twoHandedDice})` : p)));
  if (weapon.rangeNormal && weapon.rangeLong) {
    props.push(`${weapon.rangeNormal}/${weapon.rangeLong} ft`);
  } else if (weapon.throwRange && weapon.throwRangeLong) {
    props.push(`${weapon.throwRange}/${weapon.throwRangeLong} ft`);
  }

  const values = {
    name: weapon.name,
    to_hit: toHit,
    damage_dice: damageBonus === 0 ? damageDice : damageBonus > 0 ? `${damageDice}+${damageBonus}` : `${damageDice}${damageBonus}`,
    damage_type: damageType,
    properties: props.join(', '),
  };
  return {
    rowId: `weapon-${weapon.slug}`,
    // auto_sig = what the system wrote: a row whose numbers no longer match it was edited by hand (a +1 sword) and the
    // equip sync leaves it alone.
    values: { ...values, auto_sig: attackRowSignature(values) },
  };
}

/** The numbers of an attack row the system compares to tell its own rows from hand-edited ones. */
export function attackRowSignature(v: Record<string, unknown>): string {
  return [v.name, v.to_hit, v.damage_dice, v.damage_type].map(x => String(x ?? '')).join('|');
}

/** A system-generated attack row edited by hand since (its numbers differ from the signature it was written with). */
export function isHandEditedAttackRow(v: Record<string, unknown>): boolean {
  return typeof v.auto_sig === 'string' && v.auto_sig !== attackRowSignature(v);
}
